import React, { useState, useEffect } from "react";
import {
  Table,
  Text,
  Group,
  Badge,
  Pagination,
  Skeleton,
  Stack,
  Paper,
  Collapse,
  UnstyledButton,
  Button,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronRight,
  IconClock,
  IconPhoneIncoming,
} from "@tabler/icons-react";
import { useVoiceAI, VoiceAICall } from "../../context/VoiceAIContext";
import { formatPhoneNumberDisplay as formatPhoneNumber } from "../../utils/phoneUtils";

function formatDuration(seconds: number | null): string {
  if (!seconds) return "--";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function formatDateTime(isoString: string): string {
  if (!isoString) return "--";
  const date = new Date(isoString);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function CallRow({ call }: { call: VoiceAICall }) {
  const [expanded, setExpanded] = useState(false);
  const { fetchRecording } = useVoiceAI();
  const [recordingBlobUrl, setRecordingBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Revoke the object URL on unmount to free up browser memory,
    // as these URLs are not automatically cleaned up until the session ends.
    return () => {
      if (recordingBlobUrl) URL.revokeObjectURL(recordingBlobUrl);
    };
  }, [recordingBlobUrl]);

  const handleLoadRecording = async () => {
    setLoading(true);
    try {
      // Fetch the audio blob, then create a temporary object URL that acts as a local pointer
      // to the blob data within this browser session.
      const blob = await fetchRecording(call.recording_url);
      const url = URL.createObjectURL(blob);
      setRecordingBlobUrl(url);
    } catch (error) {
      console.error("Failed to load recording:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Table.Tr
        onClick={() => setExpanded(!expanded)}
        style={{ cursor: "pointer" }}
      >
        <Table.Td>
          <UnstyledButton>
            {expanded ? (
              <IconChevronDown size={14} />
            ) : (
              <IconChevronRight size={14} />
            )}
          </UnstyledButton>
        </Table.Td>
        <Table.Td>
          <Group gap="xs">
            <IconPhoneIncoming size={14} color="gray" />
            <Text size="sm">{formatPhoneNumber(call.caller_number)}</Text>
          </Group>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{formatDateTime(call.started_at)}</Text>
        </Table.Td>
        <Table.Td>
          <Group gap="xs">
            <IconClock size={14} color="gray" />
            <Text size="sm">{formatDuration(call.duration_seconds)}</Text>
          </Group>
        </Table.Td>
        <Table.Td>
          <Badge
            color={call.status === "completed" ? "green" : "yellow"}
            size="sm"
            variant="light"
          >
            {call.status}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Text size="sm" lineClamp={1} maw={300}>
            {call.summary || "--"}
          </Text>
        </Table.Td>
      </Table.Tr>
      <Table.Tr>
        <Table.Td
          colSpan={6}
          p={0}
          style={{ border: expanded ? undefined : "none" }}
        >
          <Collapse in={expanded}>
            <Paper p="md" m="xs" withBorder>
              <Stack gap="sm">
                {call.summary && (
                  <>
                    <Text fw={600} size="sm">
                      Summary
                    </Text>
                    <Text size="sm" c="dimmed">
                      {call.summary}
                    </Text>
                  </>
                )}
                {call.transcript && (
                  <>
                    <Text fw={600} size="sm">
                      Transcript
                    </Text>
                    <Paper
                      p="sm"
                      bg="gray.0"
                      style={{
                        maxHeight: 300,
                        overflow: "auto",
                        whiteSpace: "pre-wrap",
                        fontFamily: "monospace",
                        fontSize: 13,
                      }}
                    >
                      {call.transcript}
                    </Paper>
                  </>
                )}
                {call.recording_url && (
                  <>
                    <Text fw={600} size="sm">
                      Recording
                    </Text>
                    {recordingBlobUrl ? (
                      <Group>
                        <audio
                          controls
                          data-testid="audio-player"
                          src={recordingBlobUrl}
                          style={{ height: 36 }}
                        />
                      </Group>
                    ) : (
                      <Button
                        variant="light"
                        size="xs"
                        onClick={handleLoadRecording}
                        loading={loading}
                        leftSection={
                          loading ? undefined : <IconPhoneIncoming size={14} />
                        }
                      >
                        Load Recording
                      </Button>
                    )}
                  </>
                )}
              </Stack>
            </Paper>
          </Collapse>
        </Table.Td>
      </Table.Tr>
    </>
  );
}

export default function CallsTab() {
  const { calls, callsLoading, callsPagination, fetchCalls } = useVoiceAI();

  if (callsLoading) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} height={50} />
        ))}
      </Stack>
    );
  }

  if (calls.length === 0) {
    return (
      <Paper p="xl" withBorder ta="center">
        <IconPhoneIncoming size={40} color="gray" />
        <Text mt="md" c="dimmed">
          No calls yet. Once your Voice AI phone number receives calls, they
          will appear here.
        </Text>
      </Paper>
    );
  }

  return (
    <Stack gap="md">
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={40}></Table.Th>
            <Table.Th>Caller</Table.Th>
            <Table.Th>Date</Table.Th>
            <Table.Th>Duration</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Summary</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {calls.map((call) => (
            <CallRow key={call.id} call={call} />
          ))}
        </Table.Tbody>
      </Table>

      {callsPagination.total_pages > 1 && (
        <Group justify="center">
          <Pagination
            total={callsPagination.total_pages}
            value={callsPagination.page}
            onChange={(page) => fetchCalls(page)}
          />
        </Group>
      )}
    </Stack>
  );
}
